// Configurações do sistema — /configuracoes. Chaves geridas pelo backend
// (SystemSettingDefinitions.cs): WhatsApp, Chatwoot, Smtp, MediaStorage. Espelha
// C:\Users\ti4ec\Downloads\chatplatform-admin (Angular) — mesmo backend, tela irmã.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export interface SystemSettingSummary {
  key: string
  category: string
  label: string
  description: string | null
  isSecret: boolean
  hasValue: boolean
  value: string | null
  /** Chave de liga/desliga ("true"/"false"): a tela mostra um switch. toggleDefault = valor quando vazia. */
  isToggle?: boolean
  toggleDefault?: boolean
}

export interface RevealSettingResult {
  found: boolean
  value: string | null
}

function authHeaders(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export interface SettingsSlackChannel { id: string; name: string; isPrivate: boolean; isMember: boolean }
export interface SettingsSlackChannels { configured: boolean; channels: SettingsSlackChannel[]; problem: string | null }

/** Canais do Slack pro seletor das configurações (usa o Bot Token salvo em Slack). */
export async function listSettingsSlackChannels(token: string): Promise<SettingsSlackChannels> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/slack/canais`, { headers: authHeaders(token) })
    if (res.status === 401 || res.status === 403) return { configured: false, channels: [], problem: 'Sem permissão pra listar os canais.' }
    const data = await res.json().catch(() => null)
    return data && typeof data.configured === 'boolean' ? data : { configured: false, channels: [], problem: `Erro ${res.status} no servidor.` }
  } catch {
    return { configured: false, channels: [], problem: 'Não foi possível falar com o servidor.' }
  }
}

export async function listSystemSettings(token: string): Promise<SystemSettingSummary[]> {
  const res = await fetch(`${WOOTDASH_BASE}/configuracoes`, { headers: authHeaders(token) })
  if (!res.ok) throw new Error('Falha ao carregar configurações')
  return await res.json()
}

export async function updateSystemSettings(token: string, values: Record<string, string | null>): Promise<{ updatedCount: number }> {
  const res = await fetch(`${WOOTDASH_BASE}/configuracoes`, {
    method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ values }),
  })
  if (!res.ok) throw new Error('Falha ao salvar configurações')
  return await res.json()
}

export async function revealSystemSetting(token: string, key: string): Promise<RevealSettingResult> {
  const res = await fetch(`${WOOTDASH_BASE}/configuracoes/revelar?chave=${encodeURIComponent(key)}`, { headers: authHeaders(token) })
  if (!res.ok) return { found: false, value: null }
  return await res.json()
}

/** "Testar conexão" da Vertex AI: autentica a conta de serviço e chama o modelo com o que está na tela. */
export interface VertexTestResult {
  success: boolean
  problem: string | null
  stage: 'settings' | 'credential' | 'auth' | 'model' | 'network' | null
  problems: string[]
  serviceAccountEmail: string | null
  projectId: string | null
  region: string | null
  model: string | null
  latencyMs: number | null
  reply: string | null
  inputTokens: number | null
  outputTokens: number | null
}

export async function testVertexConnection(token: string, values: Record<string, string | null>): Promise<VertexTestResult> {
  const failed = (problem: string): VertexTestResult => ({
    success: false, problem, stage: 'network', problems: [problem], serviceAccountEmail: null, projectId: null,
    region: null, model: null, latencyMs: null, reply: null, inputTokens: null, outputTokens: null,
  })
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/vertex/testar`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ values }),
    })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (precisa de settings.manage).')
    const data = await res.json().catch(() => null)
    return data && typeof data.success === 'boolean' ? data : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

/** "Testar conexão" do servidor MCP: pede o catálogo de ferramentas com o que está na tela (vazio = salvo). */
export interface McpTestTool { name: string; description: string; sideEffect: boolean }
export interface McpTestResult { success: boolean; problem: string | null; serverUrl: string | null; latencyMs: number | null; tools: McpTestTool[] }

export async function testMcpConnection(token: string, values: Record<string, string | null>): Promise<McpTestResult> {
  const failed = (problem: string): McpTestResult => ({ success: false, problem, serverUrl: null, latencyMs: null, tools: [] })
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/mcp/testar`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ values }),
    })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (precisa de settings.manage).')
    const data = await res.json().catch(() => null)
    return data && typeof data.success === 'boolean' ? data : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

/** Projeto do Google Cloud que a conta de serviço enxerga (id = o que vai no campo). */
export interface VertexProject { id: string; name: string }
export interface VertexProjectsResult { success: boolean; problem: string | null; stage: string | null; projects: VertexProject[] }

export async function listVertexProjects(token: string, values: Record<string, string | null>): Promise<VertexProjectsResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/vertex/projetos`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ values }),
    })
    if (res.status === 401 || res.status === 403) return { success: false, problem: 'Sem permissão (precisa de settings.manage).', stage: null, projects: [] }
    const data = await res.json().catch(() => null)
    return data && typeof data.success === 'boolean'
      ? { ...data, projects: data.projects ?? [] }
      : { success: false, problem: `Erro ${res.status} no servidor.`, stage: null, projects: [] }
  } catch {
    return { success: false, problem: 'Não foi possível falar com o servidor.', stage: null, projects: [] }
  }
}

/** Uso de hoje contra o teto (meia-noite de Brasília) e estado do cache no Google (retenção zero). */
export interface VertexStatus {
  today: { tokens: number; calls: number; failures: number; limit: number | null; limitReached: boolean }
  cacheDisabled: boolean | null
  cacheProblem: string | null
  /** Caracteres de voz do dia contra o teto próprio (atendimento por voz). */
  voiceToday?: { characters: number; calls: number; failures: number; limit: number | null; limitReached: boolean } | null
}

export async function getVertexStatus(token: string): Promise<VertexStatus | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/vertex/status`, { headers: authHeaders(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

export async function disableVertexCache(token: string): Promise<{ cacheDisabled: boolean | null; problem: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/vertex/desligar-cache`, { method: 'POST', headers: authHeaders(token) })
    if (res.status === 401 || res.status === 403) return { cacheDisabled: null, problem: 'Sem permissão (precisa de settings.manage).' }
    const data = await res.json().catch(() => null)
    return data ?? { cacheDisabled: null, problem: `Erro ${res.status} no servidor.` }
  } catch {
    return { cacheDisabled: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

/** Admin › Saúde da Conta: o número na Meta e pra onde vão os eventos (webhook) em cada nível. */
export interface WhatsAppAccountHealth {
  displayPhoneNumber: string | null
  verifiedName: string | null
  nameStatus: string | null
  qualityRating: string | null
  messagingLimitTier: string | null
  accountMode: string | null
  status: string | null
  webhookApplication: string | null
  webhookBusinessAccount: string | null
  webhookPhoneNumber: string | null
  appSubscribed: boolean | null
  subscribedAppName: string | null
  webhookProblem: string | null
  effectiveWebhook: string | null
}

export interface WhatsAppAccountStatus {
  ok: boolean
  problem: string | null
  health: WhatsAppAccountHealth | null
  expectedWebhook: string | null
  match: 'registered' | 'different' | 'unknown'
  phoneNumberId: string | null
}

export async function getWhatsAppAccountHealth(token: string): Promise<WhatsAppAccountStatus> {
  const failed = (problem: string): WhatsAppAccountStatus => ({ ok: false, problem, health: null, expectedWebhook: null, match: 'unknown', phoneNumberId: null })
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/whatsapp/saude`, { headers: authHeaders(token) })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (precisa de settings.manage).')
    const data = await res.json().catch(() => null)
    return data && typeof data.ok === 'boolean' ? data : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}

/** Admin › Saúde da Conta: conferência da assinatura do webhook e as chamadas recusadas por ela. */
export interface WebhookRejection {
  at: string
  /** sem-assinatura | assinatura-invalida | sem-app-secret */
  reason: string
  phoneNumberId: string | null
  sender: string | null
  messages: number
  statuses: number
  remoteIp: string | null
}
export interface WebhookRejectionsView {
  requireSignature: boolean
  appSecretConfigured: boolean
  last24Hours: number
  items: WebhookRejection[]
}

export async function getWebhookRejections(token: string): Promise<WebhookRejectionsView | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/whatsapp/recusas`, { headers: authHeaders(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

export async function registerWhatsAppWebhook(token: string, url: string): Promise<{ ok: boolean; problem: string | null; status: WhatsAppAccountStatus | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/whatsapp/webhook`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ url }),
    })
    if (res.status === 401 || res.status === 403) return { ok: false, problem: 'Sem permissão (precisa de settings.manage).', status: null }
    const data = await res.json().catch(() => null)
    return res.ok ? { ok: true, problem: null, status: data?.status ?? null } : { ok: false, problem: data?.problem ?? `Erro ${res.status} no servidor.`, status: null }
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.', status: null }
  }
}

/** Configurações que todo agente lê (GET /configuracoes/publicas) — substitui get_settings do n8n. */
/** Avatar de um agente de IA (mensagens do bloco Agente IA). */
export interface AiAgentAvatar { id: string; name: string; avatarUrl: string | null }

export interface PublicSettings {
  autoLogoutMinutes: number
  restrictedTemplateSending: boolean
  customLogo: string | null
  /** Foto das mensagens do bot (Configurações Gerais › Bot › Avatar do bot). */
  botAvatar?: string | null
  aiAgents?: AiAgentAvatar[]
  /** App de celular: entrar com digital liberado (Configurações Gerais › App de celular). */
  biometricEnabled?: boolean
  /** App de celular: minutos fora até travar (0 = não trava). */
  appLockMinutes?: number
}

export async function getPublicSettings(token: string): Promise<PublicSettings | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/publicas`, { headers: authHeaders(token) })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

/** Admin › Infraestrutura (GET /configuracoes/infraestrutura). state: ok | warn | down. Só lê. */
export interface InfraDetail { label: string; value: string }
export interface InfraServiceStatus {
  key: string
  name: string
  state: 'ok' | 'warn' | 'down'
  problem: string | null
  details: InfraDetail[]
  variables: string[]
  pageUrl: string | null
  pageLabel: string | null
}
export interface InfraQueueStatus { name: string; messages: number; consumers: number; isDeadLetter: boolean; state: 'ok' | 'warn' | 'down' }
export interface InfrastructureHealth { services: InfraServiceStatus[]; queues: InfraQueueStatus[]; checkedAt: string }

export async function getInfrastructureHealth(token: string): Promise<{ data: InfrastructureHealth | null; problem: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/infraestrutura`, { headers: authHeaders(token) })
    if (res.status === 401 || res.status === 403) return { data: null, problem: 'Sem permissão (precisa de settings.manage).' }
    if (!res.ok) return { data: null, problem: `Erro ${res.status} no servidor.` }
    return { data: await res.json(), problem: null }
  } catch {
    return { data: null, problem: 'Não foi possível falar com o servidor.' }
  }
}

/* ── Admin › Fallback de atendimento ───────────────────────────────── */

export interface FallbackPreviewItem {
  id: number
  wamid: string
  waId: string
  contactName: string | null
  messageType: string
  preview: string | null
  messageAt: string | null
  receivedAt: string | null
  signatureOk: boolean
  numberOk: boolean
  alreadyImported: boolean
  problem: string | null
}
export interface FallbackPreview { ok: boolean; problem: string | null; enabled: boolean; items: FallbackPreviewItem[] }
export interface FallbackImportResult { outcome: number | string; problem: string | null; fetched: number; imported: number; duplicates: number; rejected: number }
export interface FallbackRun { id: string; trigger: 'manual' | 'auto'; at: string; fetched: number; imported: number; duplicates: number; rejected: number; problems: string | null }

export interface LinkusTestResult { ok: boolean; problem: string | null; tokenExpiresInSeconds: number | null; sampleNumber: string | null }

/** Admin › Linkus: pega um token no PABX com o Client ID/Secret salvos e devolve na hora. Não liga pra ninguém. */
export async function testLinkus(token: string, samplePhone?: string): Promise<LinkusTestResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/linkus/testar`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ samplePhone: samplePhone ?? null }),
    })
    if (!res.ok) return { ok: false, problem: res.status === 403 ? 'Sem permissão (precisa de settings.manage).' : `Erro ${res.status} no servidor.`, tokenExpiresInSeconds: null, sampleNumber: null }
    return await res.json()
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.', tokenExpiresInSeconds: null, sampleNumber: null }
  }
}

export async function previewFallback(token: string): Promise<FallbackPreview> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/fallback/pendentes`, { headers: authHeaders(token) })
    if (!res.ok) return { ok: false, problem: res.status === 403 ? 'Sem permissão (precisa de settings.manage).' : `Erro ${res.status} no servidor.`, enabled: false, items: [] }
    return await res.json()
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.', enabled: false, items: [] }
  }
}

export async function importFallback(token: string, ids: number[] | null): Promise<FallbackImportResult | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/fallback/importar`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ ids }),
    })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

export async function listFallbackRuns(token: string): Promise<FallbackRun[]> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/fallback/execucoes`, { headers: authHeaders(token) })
    return res.ok ? await res.json() : []
  } catch {
    return []
  }
}

// ── Admin › Notificação Slack (/configuracoes/alertas-slack) ──

/** Variável da mensagem. when: em qual aviso ela vem preenchida ("ambos", "sucesso" ou "falha"). */
export interface SlackAlertVariable { name: string; label: string; sample: string; when: 'ambos' | 'sucesso' | 'falha' }

/** successLabel/failureLabel nulos = o evento não tem esse resultado (SLA estourado só "acontece"). */
export interface SlackAlertEventInfo {
  key: string
  label: string
  description: string
  successLabel: string | null
  failureLabel: string | null
  defaultSuccess: string
  defaultFailure: string
  variables: SlackAlertVariable[]
  /** Exemplo de {{erro}} deste evento (prévia e teste). */
  errorSample: string
}

export interface SlackAlertRule {
  event: string
  enabled: boolean
  channel: string | null
  channelName: string | null
  onSuccess: boolean
  onFailure: boolean
  successTemplate: string | null
  failureTemplate: string | null
  updatedAt?: string | null
}

export interface SlackAlertsView { slackConfigured: boolean; events: SlackAlertEventInfo[]; rules: SlackAlertRule[]; common: SlackAlertVariable[] }

export async function listSlackAlerts(token: string): Promise<SlackAlertsView> {
  const res = await fetch(`${WOOTDASH_BASE}/configuracoes/alertas-slack`, { headers: authHeaders(token) })
  if (!res.ok) throw new Error(res.status === 403 ? 'Sem permissão (precisa de settings.manage).' : `Erro ${res.status} ao carregar.`)
  return await res.json()
}

export async function saveSlackAlerts(token: string, rules: SlackAlertRule[]): Promise<{ ok: boolean; problem: string | null; event: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/alertas-slack`, {
      method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ rules }),
    })
    const data = await res.json().catch(() => null)
    if (data && typeof data.ok === 'boolean') return { ok: data.ok, problem: data.problem ?? null, event: data.event ?? null }
    return { ok: false, problem: res.status === 403 ? 'Sem permissão (precisa de settings.manage).' : `Erro ${res.status} no servidor.`, event: null }
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.', event: null }
  }
}

export async function testSlackAlert(token: string, event: string, success: boolean, channel: string | null, template: string | null):
  Promise<{ ok: boolean; problem: string | null; text: string | null }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/alertas-slack/testar`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ event, success, channel, template }),
    })
    if (!res.ok) return { ok: false, problem: res.status === 403 ? 'Sem permissão (precisa de settings.manage).' : `Erro ${res.status} no servidor.`, text: null }
    return await res.json()
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.', text: null }
  }
}

/** Voz do atendimento (Admin › Vertex AI): label = nome curto (Neural2 A, Kore); supportsPitch = o tom vale pra ela. */
export interface VoiceOption { name: string; label: string; kind: string; gender: string; supportsPitch: boolean }
export interface VoiceListResult { success: boolean; problem: string | null; voices: VoiceOption[] }

/** Vozes em português que a conta de serviço digitada (ou salva) pode usar. Não gasta caracteres. */
export async function listVertexVoices(token: string, values: Record<string, string | null>): Promise<VoiceListResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/vertex/vozes`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ values }),
    })
    if (res.status === 401 || res.status === 403) return { success: false, problem: 'Sem permissão (precisa de settings.manage).', voices: [] }
    const data = await res.json().catch(() => null)
    return data && typeof data.success === 'boolean'
      ? { ...data, voices: data.voices ?? [] }
      : { success: false, problem: `Erro ${res.status} no servidor.`, voices: [] }
  } catch {
    return { success: false, problem: 'Não foi possível falar com o servidor.', voices: [] }
  }
}

/** ▶ Ouvir / Testar voz: áudio OGG/Opus em base64, com os caracteres gastos e o tempo. */
export interface VoiceSampleResult {
  success: boolean
  problem: string | null
  stage: string | null
  audioBase64: string | null
  mimeType: string
  voice: string | null
  characters: number
  latencyMs: number | null
}

export async function sampleVertexVoice(token: string, values: Record<string, string | null>, texto: string | null): Promise<VoiceSampleResult> {
  const failed = (problem: string): VoiceSampleResult => ({ success: false, problem, stage: null, audioBase64: null, mimeType: 'audio/ogg', voice: null, characters: 0, latencyMs: null })
  try {
    const res = await fetch(`${WOOTDASH_BASE}/configuracoes/vertex/voz/amostra`, {
      method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ values, texto }),
    })
    if (res.status === 401 || res.status === 403) return failed('Sem permissão (precisa de settings.manage).')
    const data = await res.json().catch(() => null)
    return data && typeof data.success === 'boolean' ? data : failed(`Erro ${res.status} no servidor.`)
  } catch {
    return failed('Não foi possível falar com o servidor.')
  }
}
